import React, { useMemo, useState } from 'react';
import { Row, Col, Card, Badge, Spinner } from 'react-bootstrap';
import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router-dom';
import moment from 'moment';

import ProfileLayout from 'components/marketing/instructor/ProfileLayout';
import { GET_MY_EVENT_CERTIFICATES, GET_MY_COURSE_CERTIFICATES } from 'helper/graphql/queries';
import { PATH_EVENTS, PATH_DASHBOARD } from 'routes/Paths';

const formatTypeLabel = (t) =>
	t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Event';

// Deterministic colour from string (matches EventCard palette)
const TYPE_COLOURS = ['#1d9eb0', '#F68521', '#8b5cf6', '#10b981', '#ef4444', '#f59e0b'];
const getTypeColour = (str = '') => {
	let h = 0;
	for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
	return TYPE_COLOURS[Math.abs(h) % TYPE_COLOURS.length];
};

// ─── Event certificate card ──────────────────────────────────────────────────
const EventCertCard = ({ cert }) => {
	const { event, issuedAt, certificateUrl } = cert;
	const colour = getTypeColour(event?.eventType);
	const isPast = event?.sessionDatetime && moment(event.sessionDatetime).isBefore(moment());

	return (
		<Card className="mc-card h-100">
			<div className="mc-card-bar" style={{ background: colour }} />
			<Card.Body className="d-flex flex-column p-4">
				<div className="d-flex align-items-start justify-content-between mb-3">
					<Badge className="mc-type-badge" style={{ background: `${colour}18`, color: colour }}>
						<i className="fe fe-radio me-1"></i>
						{formatTypeLabel(event?.eventType)}
					</Badge>
					{isPast && (
						<Badge bg="" className="mc-attended-badge">
							<i className="fe fe-check me-1"></i>Attended
						</Badge>
					)}
				</div>
				<h5 className="mc-cert-title mb-2">{event?.title}</h5>
				{event?.speakerName && (
					<p className="mc-speaker mb-3">
						<i className="fe fe-mic me-1"></i>{event.speakerName}
					</p>
				)}
				<div className="mc-meta mb-4">
					{event?.sessionDatetime && (
						<div className="mc-meta-row">
							<i className="fe fe-calendar me-2"></i>
							{moment(event.sessionDatetime).format('MMMM D, YYYY')}
						</div>
					)}
					{issuedAt && (
						<div className="mc-meta-row">
							<i className="fe fe-award me-2"></i>
							Issued {moment(issuedAt).format('MMMM D, YYYY')}
						</div>
					)}
				</div>
				<div className="mt-auto">
					{certificateUrl ? (
						<a href={certificateUrl} target="_blank" rel="noopener noreferrer" className="btn mc-download-btn w-100">
							<i className="fe fe-download me-2"></i>Download Certificate
						</a>
					) : (
						<div className="mc-pending">
							<i className="fe fe-clock me-2"></i>Certificate not yet issued
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

// ─── Course certificate card ─────────────────────────────────────────────────
const CourseCertCard = ({ cert }) => {
	const { course, issuedAt, certificateUrl } = cert;
	const colour = getTypeColour(course?.title || '');

	return (
		<Card className="mc-card h-100">
			<div className="mc-card-bar" style={{ background: colour }} />
			<Card.Body className="d-flex flex-column p-4">
				<div className="d-flex align-items-start justify-content-between mb-3">
					<Badge className="mc-type-badge" style={{ background: `${colour}18`, color: colour }}>
						<i className="fe fe-book me-1"></i>Course
					</Badge>
					<Badge bg="" className="mc-attended-badge">
						<i className="fe fe-check me-1"></i>Completed
					</Badge>
				</div>
				<h5 className="mc-cert-title mb-2">{course?.title}</h5>
				<div className="mc-meta mb-4">
					{course?.courseStartDate && (
						<div className="mc-meta-row">
							<i className="fe fe-calendar me-2"></i>
							{moment(course.courseStartDate).format('MMM D')} –{' '}
							{course?.courseEndDate ? moment(course.courseEndDate).format('MMM D, YYYY') : ''}
						</div>
					)}
					{issuedAt && (
						<div className="mc-meta-row">
							<i className="fe fe-award me-2"></i>
							Issued {moment(issuedAt).format('MMMM D, YYYY')}
						</div>
					)}
				</div>
				<div className="mt-auto">
					{certificateUrl ? (
						<a href={certificateUrl} target="_blank" rel="noopener noreferrer" className="btn mc-download-btn w-100">
							<i className="fe fe-download me-2"></i>Download Certificate
						</a>
					) : (
						<div className="mc-pending">
							<i className="fe fe-clock me-2"></i>Certificate not yet issued
						</div>
					)}
				</div>
			</Card.Body>
		</Card>
	);
};

// ─── Main page ───────────────────────────────────────────────────────────────
const MyCertificates = () => {
	const [activeTab, setActiveTab] = useState('all');

	const { data: eventData, loading: eventLoading, refetch: refetchEvents } = useQuery(
		GET_MY_EVENT_CERTIFICATES, { fetchPolicy: 'network-only' }
	);
	const { data: courseData, loading: courseLoading, refetch: refetchCourses } = useQuery(
		GET_MY_COURSE_CERTIFICATES, { fetchPolicy: 'network-only' }
	);

	const eventCerts = useMemo(() => eventData?.myEventCertificates || [], [eventData]);
	const courseCerts = useMemo(() => courseData?.myCourseCertificates || [], [courseData]);

	const loading = eventLoading || courseLoading;
	const total = eventCerts.length + courseCerts.length;

	const handleRefetch = () => { refetchEvents(); refetchCourses(); };

	const tabs = [
		{ key: 'all', label: 'All', count: total },
		{ key: 'events', label: 'Events', count: eventCerts.length },
		{ key: 'courses', label: 'Courses', count: courseCerts.length },
	];

	const showEvents = activeTab === 'all' || activeTab === 'events';
	const showCourses = activeTab === 'all' || activeTab === 'courses';

	return (
		<ProfileLayout>
			<div className="mc-page">
				<div className="mc-page-header mb-4">
					<h3 className="mc-page-title">My Certificates</h3>
					<p className="mc-page-sub text-muted">
						E-certificates of attendance and completion for events and courses.
					</p>
				</div>

				{/* Tabs */}
				<div className="mc-tabs mb-4">
					{tabs.map((t) => (
						<button
							key={t.key}
							className={`mc-tab-btn ${activeTab === t.key ? 'active' : ''}`}
							onClick={() => setActiveTab(t.key)}
						>
							{t.label}
							{t.count > 0 && <span className="mc-tab-count ms-1">{t.count}</span>}
						</button>
					))}
				</div>

				{loading && (
					<div className="mc-loading">
						<Spinner animation="border" size="sm" className="me-2" />
						Loading certificates…
					</div>
				)}

				{!loading && total === 0 && (
					<div className="mc-empty">
						<i className="fe fe-award mc-empty-icon"></i>
						<h5 className="mc-empty-title">No certificates yet</h5>
						<p className="text-muted mb-3">
							If you recently submitted feedback, your certificate may still be generating.
						</p>
						<button className="btn mc-browse-btn mb-3" onClick={handleRefetch}>
							<i className="fe fe-refresh-cw me-2"></i>Check Again
						</button>
						<Link to={PATH_EVENTS.root} className="btn mc-browse-alt-btn">
							<i className="fe fe-calendar me-2"></i>Browse Events
						</Link>
					</div>
				)}

				{!loading && total > 0 && (
					<>
						{/* Event certificates */}
						{showEvents && eventCerts.length > 0 && (
							<>
								{activeTab === 'all' && (
									<h6 className="mc-section-label mb-3">
										<i className="fe fe-radio me-2"></i>Event Certificates
									</h6>
								)}
								<Row className="g-4 mb-5">
									{eventCerts.map((cert) => (
										<Col key={cert.guid} xs={12} md={6} lg={4}>
											<EventCertCard cert={cert} />
										</Col>
									))}
								</Row>
							</>
						)}

						{/* Course certificates */}
						{showCourses && courseCerts.length > 0 && (
							<>
								{activeTab === 'all' && (
									<h6 className="mc-section-label mb-3">
										<i className="fe fe-book me-2"></i>Course Certificates
									</h6>
								)}
								<Row className="g-4">
									{courseCerts.map((cert) => (
										<Col key={cert.guid} xs={12} md={6} lg={4}>
											<CourseCertCard cert={cert} />
										</Col>
									))}
								</Row>
							</>
						)}

						{/* Empty for selected tab */}
						{showEvents && !showCourses && eventCerts.length === 0 && (
							<p className="text-muted text-center py-4">No event certificates yet.</p>
						)}
						{showCourses && !showEvents && courseCerts.length === 0 && (
							<p className="text-muted text-center py-4">No course certificates yet.</p>
						)}
					</>
				)}
			</div>

			<style>{`
				.mc-page-title { font-size: 1.375rem; font-weight: 800; color: #1e293b; margin-bottom: 0.25rem; }
				.mc-page-sub { font-size: 0.9375rem; }
				.mc-tabs { display: flex; gap: 0.5rem; flex-wrap: wrap; }
				.mc-tab-btn {
					background: #f1f5f9; border: 2px solid transparent; color: #64748b;
					font-size: 0.875rem; font-weight: 600; padding: 0.4rem 1rem;
					border-radius: 10px; cursor: pointer; transition: all 0.2s;
					display: inline-flex; align-items: center;
				}
				.mc-tab-btn:hover { background: white; border-color: #e2e8f0; color: #1e293b; }
				.mc-tab-btn.active {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					border-color: transparent; color: white;
					box-shadow: 0 3px 10px rgba(246,133,33,0.3);
				}
				.mc-tab-count {
					background: rgba(255,255,255,0.25);
					border-radius: 6px; font-size: 0.6875rem;
					padding: 1px 6px; font-weight: 700;
				}
				.mc-tab-btn:not(.active) .mc-tab-count {
					background: rgba(100,116,139,0.15);
					color: #64748b;
				}
				.mc-section-label {
					font-size: 0.875rem; font-weight: 700; color: #475569;
					text-transform: uppercase; letter-spacing: 0.4px;
					display: flex; align-items: center;
				}
				.mc-loading {
					display: flex; align-items: center; justify-content: center;
					padding: 3rem; color: #94a3b8; font-size: 0.875rem;
				}
				.mc-empty {
					display: flex; flex-direction: column; align-items: center;
					justify-content: center; padding: 4rem 2rem; text-align: center;
				}
				.mc-empty-icon { font-size: 3rem; color: #e2e8f0; margin-bottom: 1rem; }
				.mc-empty-title { font-size: 1.125rem; font-weight: 700; color: #1e293b; }
				.mc-browse-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px;
					padding: 0.625rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
					display: inline-flex; align-items: center;
				}
				.mc-browse-btn:hover { opacity: 0.9; color: white; }
				.mc-browse-alt-btn {
					background: none; border: 1.5px solid #e2e8f0 !important; color: #64748b !important;
					border-radius: 10px; padding: 0.625rem 1.5rem; font-weight: 600; font-size: 0.9375rem;
					display: inline-flex; align-items: center;
				}
				.mc-browse-alt-btn:hover { border-color: #94a3b8 !important; color: #1e293b !important; }
				/* Card */
				.mc-card { border: 1px solid #e2e8f0; border-radius: 16px; overflow: hidden; box-shadow: 0 2px 8px rgba(0,0,0,0.04); transition: box-shadow 0.2s; }
				.mc-card:hover { box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
				.mc-card-bar { height: 4px; }
				.mc-type-badge { font-size: 0.7rem; font-weight: 700; padding: 4px 10px; border-radius: 8px; text-transform: uppercase; letter-spacing: 0.4px; }
				.mc-attended-badge { background: rgba(16,185,129,0.1) !important; color: #10b981 !important; font-size: 0.7rem; font-weight: 700; padding: 4px 10px; border-radius: 8px; }
				.mc-cert-title { font-size: 1rem; font-weight: 700; color: #1e293b; line-height: 1.4; }
				.mc-speaker { font-size: 0.8125rem; color: #64748b; margin: 0; display: flex; align-items: center; }
				.mc-meta { display: flex; flex-direction: column; gap: 0.4rem; }
				.mc-meta-row { font-size: 0.8125rem; color: #64748b; display: flex; align-items: center; }
				.mc-download-btn {
					background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
					color: white; border: none; border-radius: 10px; padding: 0.625rem;
					font-weight: 600; font-size: 0.875rem; text-decoration: none;
					display: flex; align-items: center; justify-content: center; transition: opacity 0.2s;
				}
				.mc-download-btn:hover { opacity: 0.9; color: white; }
				.mc-pending {
					background: #f8fafc; border: 1px dashed #cbd5e1; border-radius: 10px;
					padding: 0.625rem; font-size: 0.8125rem; color: #94a3b8;
					display: flex; align-items: center; justify-content: center;
				}
			`}</style>
		</ProfileLayout>
	);
};

export default MyCertificates;
